import React, { useState, useRef, useEffect } from "react";

import { IconButton, styled } from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronCircleRight, faChevronCircleLeft } from "@fortawesome/free-solid-svg-icons";

const Container = styled("div")(() => {
  return {
    position: "relative",
    paddingLeft: "5px",
    paddingRight: "5px",
    height: "100%",
  };
});

const ScrollableContent = styled("div")(() => {
  return {
    display: "flex",
    position: "relative",
    overflowX: "auto",
    "&-ms-overflow-style": "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    height: "100%",
    overflowY: "clip",
  };
});

const LeftIcon = styled(IconButton)(() => {
  return {
    position: "absolute",
    top: "50%",
    left: "1px",
    transform: "translateY(-50 %)",
    zIndex: 1000,
  };
});

const RightIcon = styled(IconButton)(() => {
  return {
    position: "absolute",
    top: "50%",
    right: "1px",
    transform: "translateY(-50 %)",
    zIndex: 1000,
  };
});

export function HorizontalScrollContainer({ children }: React.PropsWithChildren<Record<never, any>>) {
  const parentContainerRef = useRef<HTMLDivElement>(null);
  const scrollableContentContainerRef = useRef<HTMLDivElement>(null);

  const [scrollLeftButtonEnabled, setScrollLeftButtonEnabled] = useState(false);
  const [scrollRightButtonEnabled, setScrollRightButtonEnabled] = useState(false);
  const [scrollPixelDistance, setScrollPixelDistance] = useState(500);

  useEffect(() => {
    const updateScrollStatus = () => {
      setScrollDistance();
      setScrollButtonStatus();
    };

    window.addEventListener("resize", updateScrollStatus);
    updateScrollStatus();

    return () => window.removeEventListener("resize", updateScrollStatus);
  }, []);

  function setScrollDistance() {
    const containerWidth = parentContainerRef?.current?.offsetWidth || 0;
    setScrollPixelDistance(containerWidth * 0.85);
  }

  function onScrollRightClick() {
    if (scrollRightButtonEnabled) {
      let scrollPosition = scrollableContentContainerRef?.current?.scrollLeft;
      let scrollToPosition = (scrollPosition || 0) + scrollPixelDistance;

      scrollableContentContainerRef?.current?.scrollTo({
        left: scrollToPosition,
        behavior: "smooth",
      });
    }
  }

  function onScrollLeftClick() {
    if (scrollLeftButtonEnabled) {
      let scrollPosition = scrollableContentContainerRef?.current?.scrollLeft;
      let scrollToPosition = (scrollPosition || 0) - scrollPixelDistance;

      scrollableContentContainerRef?.current?.scrollTo({
        left: scrollToPosition,
        behavior: "smooth",
      });
    }
  }

  function setScrollButtonStatus() {
    if (scrollableContentContainerRef && scrollableContentContainerRef.current) {
      const scrollWidth = scrollableContentContainerRef.current.scrollWidth; //full width of the content
      const containerWidth = scrollableContentContainerRef.current.offsetWidth; //the width of the viewing container
      const scrollPosition = scrollableContentContainerRef.current.scrollLeft; //current scroll position
      const scrollEnd = scrollWidth - containerWidth; //end of scroll position

      if (scrollWidth === containerWidth) {
        //scroll area is the same size as the viewable area (no need to scroll)
        setScrollLeftButtonEnabled(false);
        setScrollRightButtonEnabled(false);
      } else if (scrollPosition === scrollEnd) {
        //scroll position is at the end of the scrollable area
        setScrollLeftButtonEnabled(true);
        setScrollRightButtonEnabled(false);
      } else if (scrollPosition === 0) {
        //scroll position is at the start of the scrollable area
        setScrollLeftButtonEnabled(false);
        setScrollRightButtonEnabled(true);
      } else {
        //scroll position somewhere in the middle of the scrollable area
        setScrollLeftButtonEnabled(true);
        setScrollRightButtonEnabled(true);
      }
    }
  }

  return (
    <React.Fragment>
      <Container ref={parentContainerRef}>
        {scrollLeftButtonEnabled && (
          <LeftIcon onClick={onScrollLeftClick}>
            <FontAwesomeIcon icon={faChevronCircleLeft} size="lg" />
          </LeftIcon>
        )}

        <ScrollableContent ref={scrollableContentContainerRef} onScroll={setScrollButtonStatus}>
          {children}
        </ScrollableContent>

        {scrollRightButtonEnabled && (
          <RightIcon onClick={onScrollRightClick}>
            <FontAwesomeIcon icon={faChevronCircleRight} size="lg" />
          </RightIcon>
        )}
      </Container>
    </React.Fragment>
  );
}
